import { Typography } from "@mui/material";
import { formatStringForClassName, prefixClassName } from "@smartrr/shared/components/primitives/index";
import { SECTION } from "@smartrr/shared/entities/CustomerPortalTheme";
import { localize, typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import React, { useEffect, useRef, useState } from "react";

import { smartrrAccountConfig } from "@customer-app/account/config";
import { useSmartrrCustomerSelector } from "@customer-app/state/typedCustomerReduxHooks";

import { HeaderWrapper, Navigation } from "./constants";
import { LoyaltyStoreAccess } from "../AquariusInner/components/Loyalty/store";
import { useSmartrrTheme } from "../hooks/useSmartrrTheme";
import { useMobileQuery } from "../utils";

export function AquariusHeader(): JSX.Element {
  const isLoyaltyEnabled = LoyaltyStoreAccess.useIsLoyaltyEnabled();
  const customerAuth = useSmartrrCustomerSelector(state => state.auth);
  const firstName = customerAuth.liquidCustomer?.firstName;
  const email = customerAuth.liquidCustomer?.email;
  const isMobile = useMobileQuery();
  const [formattedDate, setFormattedDate] = useState("");
  const navBarRef = useRef<HTMLElement>(null);
  const theme = useSmartrrTheme();
  const hasOrders = useSmartrrCustomerSelector(state => !!state.orders.orders.data.length);

  useEffect(() => {
    if (customerAuth?.customerRelationship?.createdDate) {
      setFormattedDate(
        localize("utils.dates.toViewDateMonthYear", {
          date: new Date(Date.parse(customerAuth.customerRelationship.createdDate)),
        })
      );
    }
  }, [customerAuth]);

  useEffect(() => {
    const navCallback = smartrrAccountConfig.navigationBarCreatedCallback;
    if (navCallback) {
      navCallback(navBarRef.current);
    }
  }, []);

  const sectionOrder = theme.modernThemeSettings?.sectionsOrder;

  const sections: { title: string; url: string; index: number }[] = [
    ...(sectionOrder &&
    sectionOrder.includes(SECTION.SUBSCRIPTION) &&
    theme.modernThemeSettings?.sections.subscriptions.display
      ? [
          {
            title: typedLocalize("header.tabs.subscriptions"),
            url: "#subscriptions",
            index: sectionOrder.indexOf(SECTION.SUBSCRIPTION),
          },
        ]
      : []),
    ...(sectionOrder &&
    sectionOrder.includes(SECTION.ORDERHISTORY) &&
    theme.modernThemeSettings?.sections.orderHistory.display
      ? [
          {
            title: typedLocalize("header.tabs.orderHistory"),
            url: "#order-history",
            index: sectionOrder.indexOf(SECTION.ORDERHISTORY),
          },
        ]
      : []),
    ...(sectionOrder &&
    sectionOrder.includes(SECTION.SETTINGS) &&
    theme.modernThemeSettings?.sections.settings.display
      ? [
          {
            title: typedLocalize("header.tabs.settings"),
            url: "#settings",
            index: sectionOrder.indexOf(SECTION.SETTINGS),
          },
        ]
      : []),
    ...(sectionOrder &&
    sectionOrder.includes(SECTION.LOYALTY) &&
    isLoyaltyEnabled &&
    theme.modernThemeSettings?.sections.rewards.display
      ? [
          {
            title: typedLocalize("header.tabs.loyalty"),
            url: "#loyalty",
            index: sectionOrder.indexOf(SECTION.LOYALTY),
          },
        ]
      : []),
    ...(sectionOrder &&
    sectionOrder.includes(SECTION.TRENDING) &&
    theme.modernThemeSettings?.sections.trending.display
      ? [
          {
            title: typedLocalize("header.tabs.trending"),
            url: "#trending",
            index: sectionOrder.indexOf(SECTION.TRENDING),
          },
        ]
      : []),
    ...(sectionOrder &&
    sectionOrder.includes(SECTION.INSTAGRAM) &&
    theme.modernThemeSettings?.sections.instagram?.display
      ? [
          {
            title: typedLocalize("header.tabs.social"),
            url: "#social",
            index: sectionOrder.indexOf(SECTION.INSTAGRAM),
          },
        ]
      : []),
  ]
    .map((s, i) => {
      return s;
    })
    .sort((a, b) => a?.index! - b?.index!);

  const navOptions = sections.filter(item => item != null);

  return (
    <HeaderWrapper className={prefixClassName("header")} id="smartrr_header">
      <div className={prefixClassName("customer-header")}>
        {firstName && theme.modernThemeSettings?.welcomeInfo.customerFirstName ? (
          <Typography
            variant="h1"
            className={prefixClassName("customer-name")}
          >{`${theme.modernThemeSettings?.welcomeInfo.welcomeMessage}, ${firstName}`}</Typography>
        ) : null}
        {theme.modernThemeSettings?.welcomeInfo.customerEmail ? (
          <Typography variant="body1" className={prefixClassName("customer-email")}>
            {email}
          </Typography>
        ) : null}

        {hasOrders && theme.modernThemeSettings?.welcomeInfo.customerSinceDate ? (
          <Typography variant="body1" className={prefixClassName("customer-since")}>{`${localize(
            "header.customerSince"
          )} ${formattedDate}`}</Typography>
        ) : null}
      </div>

      {theme.modernThemeSettings?.showHide.showPortalMenu ? (
        <Navigation ref={navBarRef} className={prefixClassName("navigation-bar")} isMobile={isMobile}>
          <ul>
            {navOptions.map(e => {
              return (
                <li className={prefixClassName(formatStringForClassName(`navbar-${e.title}`))} key={e.title}>
                  <a href={e.url}>{e.title}</a>
                </li>
              );
            })}
            <li className={prefixClassName(formatStringForClassName("navbar-logout"))}>
              <a href="/account/logout">{localize("header.tabs.logOut")}</a>
            </li>
          </ul>
        </Navigation>
      ) : null}
    </HeaderWrapper>
  );
}
